import React, { Fragment, useCallback } from 'react';
import { object } from 'prop-types';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { Trans, t } from '@lingui/macro';
import { I18n } from '@lingui/react';
import { navigate } from '@reach/router';
import { linkTo } from 'utils/links';
import classNames from 'classnames';
import Config from 'config';

// Redux
import { getTitle, getConfig } from 'store/modules/app/selectors';
import { getCurrentUser } from 'store/modules/auth/selectors';
import { logout } from 'store/modules/auth/actions';

// Components
import Button from 'components/Button';
import Image from 'components/Image';
import Dropdown from 'components/Dropdown';

const mapState = state => ({
  config: getConfig(state),
  user: getCurrentUser(state),
  title: getTitle(state),
});

export default function TopNav() {
  const { config, title, user } = useMappedState(mapState);
  const dispatch = useDispatch();
  const logoutUser = useCallback(() => dispatch(logout()), []);
  const isLoggedIn = !!user.id;

  return (
    <section data-test="topnav" className={classNames('TopNav flex w-full md:ml-6 lg:ml-0', {
      'px-4 py-4 md:px-8': !config.IS_NATIVE,
    })}>
      <div className="flex-1 flex items-center">
        {!config.IS_NATIVE &&
          <h2 data-test="page-title" className="hidden mt-1 font-thin text-xl text-default uppercase md:block">{title}</h2>
        }
      </div>
      {!config.IS_NATIVE &&
        <div className="flex items-center">
          {isLoggedIn &&
            <I18n>
              {({ i18n }) =>
                <UserMenu i18n={i18n} user={user} onLogout={logoutUser} />
              }
            </I18n>
          }
          {!isLoggedIn &&
            <Fragment>
              <Button test="login-button" to={linkTo('/auth/sign-in')} type="secondary" className="block mx-2"><Trans>Login</Trans></Button>
              {Config.app.APP_SIGNUPS_ENABLED === 'Enabled' &&
                <Button test="signup-button" to={linkTo('/auth/sign-up')} type="primary" className="block"><Trans>Sign Up</Trans></Button>
              }
            </Fragment>
          }
        </div>
      }
    </section>
  );
}

TopNav.propTypes = {
  user: object,
};

function UserMenu({ i18n, user, onLogout }) {
  const options = [
    { value: 'profile', url: '/members/profiles/:slug', label: i18n._(t`My Profile`) },
    { value: 'inbox', url: '/members/mailbox/inbox', label: i18n._(t`Inbox`) },
    { value: 'settings', url: '/members/profiles/:slug/settings', label: i18n._(t`Settings`) },
    { value: 'logout', url: '/auth/sign-in', label: i18n._(t`Logout`) },
  ];
  const onSelect = (name, selection) => {
    if (selection.value === 'logout') {
      onLogout();
    }
    navigate(linkTo(selection.url, user));
  };

  return (
    <Dropdown options={options} onChangeSelection={onSelect} style={{ maxWidth: '150px', minWidth: 115 }}>
      <Image src={user.image} className="rounded-full float-left" style={{ width: '25px', height: '25px' }} />
      <span className="block truncate" style={{ marginLeft: '35px' }}>{user.name}</span>
    </Dropdown>
  );
}
